/**
 * What the browser and the phone share when a catch posts a `SystemError` row through the report route (piece 9;
 * decisions 0822, 2200, 11620; row T3701): the body's shape, a caught fault's kind, message and stack, the key that
 * tells one held report from another, and the fold that holds a list of them to a ceiling. Each client keeps its own
 * transport, its own held list and its own page cap; only the pure half stands here, so the two cannot drift.
 */

/** The report's body, the browser's own shape (decision 0822). The browser adds the request id and the page's origin to it. */
export interface CaughtReportBody {
  errorKind: string;
  message: string;
  stack: string | null;
  page: string;
  appVersion: string;
  /** 🔒 Set to Refusal on a refusal the client showed the person, which the server writes as source Refusal (decision 11940). */
  source?: typeof REFUSAL_REPORT_SOURCE;
}

/** The report's source on a refusal a client showed (decision 11940); a fault's report carries none. */
export const REFUSAL_REPORT_SOURCE = 'Refusal' as const;

/**
 * 🔒 Whether an answer a client hands a screen is a refusal it reports (decision 11940): a status from 400 to 499, but
 * never 401, which is the session ending and is answered by the sign-in rather than shown as a refusal.
 */
export function isShownRefusal(status: number): boolean {
  return status >= 400 && status < 500 && status !== 401;
}

/**
 * 🔒 The body a refusal shown to the person posts (decision 11940): the status as the kind, the sentence the person
 * read as the message, no stack, and source Refusal. The server sanitises it as it does a fault's.
 */
export function refusalReportBody(input: { status: number; sentence: string | null; page: string; appVersion: string }): CaughtReportBody {
  const sentence = (input.sentence ?? '').trim();
  return {
    errorKind: String(input.status),
    message: sentence === '' ? `The server answered ${input.status}.` : sentence,
    stack: null,
    page: input.page,
    appVersion: input.appVersion,
    source: REFUSAL_REPORT_SOURCE,
  };
}

/** A read the caller cancelled is the screen moving on, not a fault. */
export function isAbortError(reason: unknown): boolean {
  return typeof reason === 'object' && reason !== null && (reason as { name?: unknown }).name === 'AbortError';
}

/** A caught fault's kind: where it was caught, then the error's own class, as the server's kind allows (decision 2200). */
export function caughtKind(where: string | null, kind: string): string {
  return where ? `${where.replace(/[^A-Za-z0-9_$.]/g, '.')}.${kind}` : kind;
}

/**
 * The body a caught fault posts. `where` names the catch (null for the browser's unhandled-rejection listener);
 * `extraStack` rides beside the error's own stack, so a plain value thrown with no stack still reads as the
 * application's own fault.
 */
export function caughtReportBody(input: { where: string | null; error: unknown; extraStack: string | null; page: string; appVersion: string }): CaughtReportBody {
  const asError = input.error instanceof Error ? input.error : null;
  return {
    errorKind: caughtKind(input.where, asError ? asError.name || asError.constructor.name : input.where ? 'Caught' : 'UnhandledRejection'),
    message: `${input.where ? `${input.where}: ` : ''}${asError ? asError.message : String(input.error)}`,
    stack: [asError?.stack ?? null, input.extraStack].filter(Boolean).join('\n') || null,
    page: input.page,
    appVersion: input.appVersion,
  };
}

/** The key that tells one held report from another: a fault held twice is held once. */
export function caughtReportKey(report: Pick<CaughtReportBody, 'errorKind' | 'message' | 'page'>): string {
  return `${report.errorKind}\n${report.message}\n${report.page}`;
}

/** The held list with these added, a fault held twice held once, the oldest dropped past the ceiling. */
export function foldHeldReports<T extends Pick<CaughtReportBody, 'errorKind' | 'message' | 'page'>>(list: readonly T[], added: readonly T[], ceiling: number): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const report of [...list, ...added]) {
    const key = caughtReportKey(report);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(report);
  }
  return out.slice(-ceiling);
}
